import React, { useState, useMemo, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Chip,
  ListItemSecondaryAction,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DSBox from "../../../system-design/DSBox";
import DSList from "../../../system-design/DSList";
import DSListItem from "../../../system-design/DSListItem";
import DSTypography from "../../../system-design/DSTypography";
import DSButton from "../../../system-design/DSButton";
import DSIconButton from "../../../system-design/DSIconButton";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import useClockDetailsByIds from "../hooks/useClockDetailsByIds";
import useClocksInfiniteQuery from "../../../Device/hooks/useClocksInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../../Device/hooks/useListboxInfiniteScroll";
import { ClocksListResponse } from "../../../../Types";

interface ClockManagementProps {
  clockIds: string[];
  onAddClock: (clockId: string) => Promise<unknown>;
  onRemoveClock: (clockId: string) => void;
  onClearAllClocks?: () => void;
  isUpdating: boolean;
  onClockClick?: (clockId: string) => void;
  includeNonRegistered?: boolean;
  emptyText?: string;
}

const ClockManagement: React.FC<ClockManagementProps> = ({
  clockIds,
  onAddClock,
  onRemoveClock,
  onClearAllClocks,
  isUpdating,
  onClockClick,
  includeNonRegistered = false,
  emptyText = "No clocks attached",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClockForAdd, setSelectedClockForAdd] =
    useState<ClocksListResponse | null>(null);
  const [isAddingClock, setIsAddingClock] = useState(false);

  // Fetch details for currently attached clocks
  const { data: attachedClocks, isLoading: isLoadingAttachedClocks } =
    useClockDetailsByIds(clockIds);

  // Fetch available clocks for search
  const {
    data: availableClocks,
    isLoading: isLoadingAvailableClocks,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({
    query: searchQuery,
    registrationStatus: includeNonRegistered ? "ALL" : "REGISTERED",
  });

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  // Filter out already attached clocks from available clocks
  const filteredAvailableClocks = useMemo(() => {
    if (!availableClocks) return [];
    const attachedIds = new Set(clockIds);
    return availableClocks.filter((clock) => !attachedIds.has(clock.id));
  }, [availableClocks, clockIds]);

  const handleAddClock = useCallback(async () => {
    if (!selectedClockForAdd) return;

    setIsAddingClock(true);
    try {
      await onAddClock(selectedClockForAdd.id);
      setSelectedClockForAdd(null);
      setSearchQuery("");
    } finally {
      setIsAddingClock(false);
    }
  }, [selectedClockForAdd, onAddClock]);

  const isOnline = useCallback((heartbeat: string) => {
    const now = new Date();
    const lastHeartbeat = new Date(heartbeat);
    const diffMinutes = (now.getTime() - lastHeartbeat.getTime()) / (1000 * 60);
    return diffMinutes < 5; // Consider online if heartbeat within 5 minutes
  }, []);

  return (
    <DSBox sx={{ mt: 2 }}>
      <DSTypography
        variant="h6"
        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
      >
        <AccessTimeIcon />
        Clock Management
      </DSTypography>

      {/* Current Clocks Section */}
      <DSBox sx={{ mb: 3 }}>
        <DSTypography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          Attached Clocks ({clockIds.length})
        </DSTypography>

        {isLoadingAttachedClocks ? (
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <DSCircularProgress size={20} />
            <DSTypography variant="body2">
              Loading clock details...
            </DSTypography>
          </DSBox>
        ) : clockIds.length === 0 ? (
          <DSBox
            sx={{
              p: 2,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <DSTypography variant="body2">{emptyText}</DSTypography>
          </DSBox>
        ) : (
          <DSList dense>
            {attachedClocks?.map((clock) => (
              <DSListItem key={clock.id} divider>
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexGrow: 1,
                    cursor: onClockClick ? "pointer" : "default",
                  }}
                  onClick={() => onClockClick?.(clock.id)}
                >
                  <Chip
                    size="small"
                    label={isOnline(clock.heartbeat) ? "Online" : "Offline"}
                    color={isOnline(clock.heartbeat) ? "success" : "default"}
                    variant="outlined"
                  />
                  <DSBox>
                    <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                      {clock.currentIdf || clock.idf}
                    </DSTypography>
                    <DSTypography variant="caption" color="text.secondary">
                      ID: {clock.id}
                      {clock.location &&
                        ` • ${clock.location.city}, ${clock.location.country_code}`}
                    </DSTypography>
                  </DSBox>
                </DSBox>
                <ListItemSecondaryAction>
                  <DSIconButton
                    edge="end"
                    size="small"
                    onClick={() => onRemoveClock(clock.id)}
                    disabled={isUpdating}
                  >
                    <DeleteIcon />
                  </DSIconButton>
                </ListItemSecondaryAction>
              </DSListItem>
            ))}
          </DSList>
        )}

        {onClearAllClocks && clockIds.length > 0 && (
          <DSBox sx={{ mt: 1 }}>
            <DSButton
              variant="outlined"
              size="small"
              color="error"
              onClick={onClearAllClocks}
              disabled={isUpdating}
            >
              Clear All Clocks
            </DSButton>
          </DSBox>
        )}
      </DSBox>

      {/* Add Clock Section */}
      <DSBox sx={{ mb: 2 }}>
        <DSTypography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          Add Clock
        </DSTypography>

        <DSBox sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <Autocomplete
            sx={{ flexGrow: 1 }}
            options={filteredAvailableClocks || []}
            filterOptions={noClientFilter}
            getOptionLabel={(option) => `${option.idf} (${option.id})`}
            value={selectedClockForAdd}
            onChange={(_, newValue) => setSelectedClockForAdd(newValue)}
            inputValue={searchQuery}
            onInputChange={(_, newInputValue) => setSearchQuery(newInputValue)}
            loading={isLoadingAvailableClocks || isFetchingNextPage}
            ListboxProps={listboxProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for clocks..."
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingAvailableClocks && (
                        <DSCircularProgress size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <DSBox>
                  <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                    {option.idf}
                  </DSTypography>
                  <DSTypography variant="caption" color="text.secondary">
                    ID: {option.id}
                  </DSTypography>
                </DSBox>
              </li>
            )}
            noOptionsText={
              searchQuery ? "No clocks found" : "Type to search for clocks"
            }
          />

          <DSButton
            variant="contained"
            size="medium"
            startIcon={
              isAddingClock ? <DSCircularProgress size={16} /> : <AddIcon />
            }
            onClick={handleAddClock}
            disabled={!selectedClockForAdd || isAddingClock || isUpdating}
          >
            Add
          </DSButton>
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default ClockManagement;
